import React from 'react';
import { useIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, Line, LineChart, Pie, PieChart, XAxis, YAxis } from 'recharts';
import EmptyState from 'components/EmptyState';
import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from 'components/ui/chart';
import { ChartSpec } from 'types/aiChat';
import { cn } from 'utils/cn';
import { getIntl } from 'utils/getTranslations';
import { ChartColumn, ChartRow, chartRows, chartSeries } from '../../utils/chartSeries';
import ChartErrorBoundary from './ChartErrorBoundary';

interface ChartToolPartProps {
    spec: ChartSpec;
}

const MAX_LABEL_LENGTH = 14;
// ~60 chars fit horizontally across the chart width at default font size
const HORIZONTAL_CHAR_BUDGET = 60;
const LABEL_PADDING = 2;
// Months, quarters and years lie flat, as the Figure draws them: where they do not all fit, the axis skips some.
const SHORT_LABEL_LENGTH = 4;
const CHART_HEIGHT = 232;
// One pie legend entry: a text-xs line (16px) plus the gap-2 between entries.
const LEGEND_ENTRY_HEIGHT = 24;

// The category axis is the plot's floor and the grid lines are faint, as the Figure draws them; the kit colours the ticks.
const AXIS_CLASSES =
    '[&_.recharts-cartesian-axis-line]:stroke-border [&_.recharts-cartesian-grid_line]:stroke-divider-soft';

// Bars round their top corners only, the legend's swatches match them, and bars never grow wider than this.
const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];
const MAX_BAR_SIZE = 24;
const LEGEND_CLASS_NAME =
    'text-caption text-foreground-secondary justify-start [&>div>div]:size-2.5 [&>div>div]:rounded-[3px]';

// The legend lists every slice beside the pie, so past nine slices the chart grows to fit it.
function pieHeight(slices: number) {
    return Math.max(CHART_HEIGHT, slices * LEGEND_ENTRY_HEIGHT);
}

// Estimate whether horizontal labels would overlap based on total character density.
// e.g. 6 bars × (4-char year + 2 padding) = 36 < 60 → horizontal; 10 × (14 + 2) = 160 → angled
function needsAngledLabels(labels: string[]) {
    const maxLen = Math.max(...labels.map((l) => Math.min(l.length, MAX_LABEL_LENGTH)));
    return maxLen > SHORT_LABEL_LENGTH && labels.length * (maxLen + LABEL_PADDING) >= HORIZONTAL_CHAR_BUDGET;
}

// Ticks only: the tooltip reads the row's own category, so it keeps the full label.
function truncateLabel(value: string) {
    return value.length > MAX_LABEL_LENGTH ? `${value.slice(0, MAX_LABEL_LENGTH)}…` : value;
}

// Recharts' default 'adaptive' steps come in 0.05 × 10ⁿ increments (0, 950, 1900…); snap125 keeps
// 1-2-2.5-5 steps (0, 1000, 2000…) at the cost of some headroom when the maximum just passes a step.
const NICE_TICKS = 'snap125';

const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: 5 };
// Half a text-xs character with some slack (digits measure ~7px): the room a centred label needs past its tick.
const HALF_CHAR_WIDTH = 4;

// A line's last point sits on the plot's right edge, so its label pokes out by half its width. Without
// that room Recharts shifts the label inwards and then hides the neighbour it now collides with.
function tailLabelRoom(labels: string[]) {
    const last = truncateLabel(labels[labels.length - 1] ?? '');
    return Math.max(CHART_MARGIN.right, Math.ceil(last.length * HALF_CHAR_WIDTH));
}

// The value axis is a fixed 60px wide, so a raw 1500000 would overflow it.
function formatYAxisValue(value: number) {
    return getIntl().formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });
}

/**
 * Whether the spec carries any data point at all. The type guards below test `spec.x && spec.series`,
 * and `[]` is truthy — so an empty result used to render a blank chart frame with no message, while
 * only a MISSING field reached the error fallback. Neither said "no data".
 */
export function hasChartData(spec: ChartSpec): boolean {
    // The pie draws only positive slices, so one with none draws no sector at all, only its legend.
    if (spec.type === 'pie') return spec.data?.some((datum) => datum.value > 0) === true;
    return (spec.x?.length ?? 0) > 0 && spec.series?.some((series) => series.data.length > 0) === true;
}

interface CategoryTipProps {
    /** Handed in by Recharts, which clones the content element. */
    active?: boolean;
    payload?: ReadonlyArray<{ dataKey?: unknown; value?: unknown; payload?: ChartRow }>;
    series: ChartColumn[];
    formatValue: (value: number) => string;
}

/**
 * The tooltip on one line: the row's category with the latest series' name, its value, then each earlier series' name
 * and value, all in the chart's unit ("Sep 2026 · 239 h · 2025: 210 h").
 */
export function CategoryTip({ active, payload, series, formatValue }: CategoryTipProps) {
    if (!active || !payload?.length) return null;
    const name = (key: unknown) => series.find((column) => column.key === key)?.label ?? String(key);
    const [latest, ...earlier] = [...payload].reverse();
    const category = latest.payload?.category ?? '';
    return (
        <div className="bg-card text-caption text-foreground shadow-tooltip rounded-[4px] px-2 py-1 whitespace-nowrap tabular-nums">
            <span className="font-semibold">{earlier.length ? `${category} ${name(latest.dataKey)}` : category}</span>
            {` · ${formatValue(Number(latest.value))}`}
            {earlier.length ? (
                <span className="text-foreground-tertiary">
                    {earlier.map((item) => ` · ${name(item.dataKey)}: ${formatValue(Number(item.value))}`).join('')}
                </span>
            ) : null}
        </div>
    );
}

const legend = (
    <ChartLegend
        verticalAlign="top"
        align="left"
        itemSorter={null}
        content={<ChartLegendContent className={LEGEND_CLASS_NAME} />}
    />
);

function renderChart(
    spec: ChartSpec,
    rows: ChartRow[],
    series: ChartColumn[],
    angled: boolean,
    formatValue: (value: number) => string
) {
    const categoryTooltip = <ChartTooltip content={<CategoryTip series={series} formatValue={formatValue} />} />;
    if (spec.type === 'bar' && spec.x && spec.series) {
        return (
            // `sign` stacks negative values down from zero rather than onto the positive total.
            <BarChart data={rows} accessibilityLayer={false} stackOffset="sign">
                <CartesianGrid vertical={false} />
                <XAxis
                    dataKey="category"
                    tickLine={false}
                    tickFormatter={truncateLabel}
                    // Every label when angled, up to ~20 bars; beyond that, skip overlapping ones.
                    interval={angled && spec.x.length <= 20 ? 0 : 'preserveEnd'}
                    {...(angled ? { angle: -35, textAnchor: 'end', height: 80 } : {})}
                />
                {/* The default value domain already includes zero, which is what zeroBaseline asks for. */}
                <YAxis tickFormatter={formatYAxisValue} niceTicks={NICE_TICKS} axisLine={false} tickLine={false} />
                {categoryTooltip}
                {legend}
                {series.map((column, index) => (
                    <Bar
                        key={column.key}
                        dataKey={column.key}
                        fill={column.color}
                        stackId={column.stackId}
                        // A stack rounds its top segment only.
                        radius={column.stackId && index < series.length - 1 ? 0 : BAR_RADIUS}
                        maxBarSize={MAX_BAR_SIZE}
                        isAnimationActive={false}
                    />
                ))}
            </BarChart>
        );
    }

    if (spec.type === 'line' && spec.x && spec.series) {
        return (
            <LineChart
                data={rows}
                accessibilityLayer={false}
                margin={{ ...CHART_MARGIN, right: tailLabelRoom(spec.x) }}
            >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="category" tickLine={false} tickFormatter={truncateLabel} />
                <YAxis
                    tickFormatter={formatYAxisValue}
                    niceTicks={NICE_TICKS}
                    axisLine={false}
                    tickLine={false}
                    domain={spec.zeroBaseline ? [0, 'auto'] : ['auto', 'auto']}
                />
                {categoryTooltip}
                {legend}
                {series.map((column) => (
                    <Line
                        key={column.key}
                        dataKey={column.key}
                        stroke={column.color}
                        strokeWidth={2}
                        type="monotone"
                        isAnimationActive={false}
                    />
                ))}
            </LineChart>
        );
    }

    if (spec.type === 'pie' && spec.data) {
        return (
            <PieChart accessibilityLayer={false}>
                <ChartTooltip content={<ChartTooltipContent nameKey="slice" hideLabel />} />
                <ChartLegend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    itemSorter={null}
                    content={
                        <ChartLegendContent
                            nameKey="slice"
                            className={cn(LEGEND_CLASS_NAME, 'flex-col items-start gap-2 pt-0')}
                        />
                    }
                />
                {/* Starts at 12 o'clock and runs clockwise. Recharts sums raw values, so a negative slice
                    would blank or invert the rest: it is drawn as zero, and the data table keeps its value. */}
                <Pie
                    data={rows}
                    dataKey={(row: ChartRow) => Math.max(0, Number(row.value))}
                    nameKey="category"
                    // The pie keeps its size in a chart of the default height when the legend makes the chart taller.
                    outerRadius={`${(100 * CHART_HEIGHT) / pieHeight(rows.length)}%`}
                    startAngle={90}
                    endAngle={-270}
                    stroke="var(--color-card)"
                    rootTabIndex={-1}
                    isAnimationActive={false}
                />
            </PieChart>
        );
    }

    return null;
}

/** The chart's numbers as a visually hidden table, so a screen reader reaches what the SVG only draws. */
function ChartDataTable({ columns, rows }: { columns: Pick<ChartColumn, 'key' | 'label'>[]; rows: ChartRow[] }) {
    return (
        <table className="sr-only">
            <thead>
                <tr>
                    <td />
                    {columns.map((column) => (
                        <th key={column.key} scope="col">
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    // oxlint-disable-next-line react-doctor/no-array-index-as-key -- categories come from the AI and may repeat; the rows never reorder
                    <tr key={index}>
                        <th scope="row">{row.category}</th>
                        {columns.map((column) => (
                            <td key={column.key}>{row[column.key]}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

interface ChartPlotProps {
    spec: ChartSpec;
    /** What a screen reader hears for the plot, which it reads as one image. */
    label: string;
    /** Shown in place of a plot the spec cannot draw. */
    fallback: React.ReactElement;
    /** The full-size dialog's taller plot. */
    tall?: boolean;
    /** A value as the tooltip gives it, in the chart's unit; a plain number by default. */
    formatValue?: (value: number) => string;
}

/** The chart itself, one image to assistive tech: its legend, axes and series, and the tooltip on hover. */
export function ChartPlot({ spec, label, fallback, tall, formatValue }: ChartPlotProps) {
    const intl = useIntl();
    const rows = chartRows(spec);
    const series = chartSeries(spec);
    const angled = spec.type === 'bar' && !!spec.x && needsAngledLabels(spec.x);
    const chart = renderChart(
        spec,
        rows,
        series,
        angled,
        formatValue ?? ((value) => intl.formatNumber(value, { maximumFractionDigits: 2 }))
    );
    if (!chart) return fallback;

    const isPie = spec.type === 'pie';
    const legendEntries = isPie ? rows.map((row) => ({ key: String(row.slice), label: row.category })) : series;
    const config: ChartConfig = Object.fromEntries(legendEntries.map(({ key, label }) => [key, { label }]));
    let height = angled ? 'h-80' : 'h-58';
    if (tall) {
        height = 'h-[min(440px,50dvh)]';
    }

    return (
        <ChartContainer
            config={config}
            role="img"
            aria-label={label}
            className={cn('aspect-auto w-full', AXIS_CLASSES, height)}
            style={isPie && !tall ? { height: pieHeight(rows.length) } : undefined}
        >
            {chart}
        </ChartContainer>
    );
}

interface ChartBodyProps {
    spec: ChartSpec;
    fallback: React.ReactElement;
}

function ChartBody({ spec, fallback }: ChartBodyProps) {
    const intl = useIntl();

    if (!hasChartData(spec)) {
        return <EmptyState size="inline" titleId="ai-chat.chart.no-data" surface="ai-chat-chart" />;
    }

    const rows = chartRows(spec);
    const columns =
        spec.type === 'pie'
            ? [{ key: 'value', label: intl.formatMessage({ id: 'ai-chat.chart.value' }) }]
            : chartSeries(spec);
    const summary =
        spec.title || intl.formatMessage({ id: 'ai-chat.chart.summary' }, { type: spec.type, count: rows.length });

    return (
        <>
            <ChartPlot spec={spec} label={summary} fallback={fallback} />
            <ChartDataTable columns={columns} rows={rows} />
        </>
    );
}

function ChartToolPart({ spec }: ChartToolPartProps) {
    const intl = useIntl();
    const resetKey = `${spec.type}:${spec.title || ''}:${spec.x?.length || 0}:${spec.series?.length || 0}:${
        spec.data?.length || 0
    }`;
    const fallback = (
        <p className="text-body2 text-error mt-2">{intl.formatMessage({ id: 'ai-chat.error.chart-render-failed' })}</p>
    );

    const handleChartError = (error: Error) => {
        console.error('Failed to render chart output in AI chat', {
            error,
            specType: spec.type,
            specTitle: spec.title,
        });
    };

    return (
        // The ::before spacer gives the card a 400px intrinsic width: a box that shrink-wraps its content would
        // otherwise leave the chart, which sizes to the card, no room. max-w-full lets it narrow in a smaller box.
        <div className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden mt-2 rounded-md border p-3 before:block before:w-100 before:max-w-full">
            {spec.title ? (
                <p className={cn('text-subtitle2', spec.description ? 'mb-1' : 'mb-2')}>{spec.title}</p>
            ) : null}
            {spec.description ? <p className="text-caption text-fg-secondary mb-3">{spec.description}</p> : null}
            <ChartErrorBoundary resetKey={resetKey} fallback={fallback} onError={handleChartError}>
                <ChartBody spec={spec} fallback={fallback} />
            </ChartErrorBoundary>
        </div>
    );
}

// Every streamed token rebuilds an equal spec, which need not draw the chart again.
// parseChartSpec builds specs with a fixed key order, so equal specs serialise identically.
export default React.memo(ChartToolPart, (prev, next) => JSON.stringify(prev.spec) === JSON.stringify(next.spec));
